import type { ReactNode } from 'react'

import { Stack, Typography } from '@mui/material'

/**
 * A grid cell carrying a primary value with a secondary one beneath it.
 *
 * Extracted on the second hand-roll (§36): the GTM outreach campaign table had
 * written this inline, and the Leads table needed the same shape for two pairs at
 * once — company over job title, email over phone. Two columns' worth of
 * information in one column's width, which is the whole reason to reach for it:
 * a field that is only ever read ALONGSIDE another one does not earn a column of
 * its own, and a grid that grows a column per field stops being scannable (§49
 * is about the header fitting, and the cheapest way to keep headers fitting is to
 * have fewer of them).
 *
 * Both lines `noWrap`. A cell that wraps changes the row height for every other
 * column in that row, so the truncation is deliberate — §49's carve-out that a
 * CELL value may ellipsize while a header may not. `title` puts the full text a
 * hover away when the caller has something longer to say.
 *
 * `bottom` is optional rather than required-but-nullable so a caller with only
 * one value renders a plain single-line cell through the same component instead
 * of branching at the call site.
 */
export const TwoLineCell = ({
  top,
  bottom,
  title,
}: {
  top: ReactNode
  bottom?: ReactNode
  title?: string
}) => (
  <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }} title={title}>
    <Typography variant="body2" noWrap>
      {top}
    </Typography>
    {bottom ? (
      <Typography variant="body3" color="text.secondary" noWrap>
        {bottom}
      </Typography>
    ) : null}
  </Stack>
)
